import dayjs, { Dayjs } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(duration);

export default dayjs;

// Common date formats
export const DATE_FORMATS = {
  DATE: 'YYYY-MM-DD',
  DATETIME: 'YYYY-MM-DD HH:mm:ss',
  TIME: 'HH:mm:ss',
  DISPLAY_DATE: 'MMM DD, YYYY',
  DISPLAY_DATETIME: 'MMM DD, YYYY HH:mm',
} as const;

// Utility functions
export function formatDate(date: dayjs.ConfigType, format: string = DATE_FORMATS.DATE): string {
  return dayjs(date).format(format);
}

export function formatDateTime(date: dayjs.ConfigType, format: string = DATE_FORMATS.DATETIME): string {
  return dayjs(date).format(format);
}

export function getRelativeTime(date: dayjs.ConfigType): string {
  return dayjs(date).fromNow();
}

export function isToday(date: dayjs.ConfigType): boolean {
  return dayjs(date).isSame(dayjs(), 'day');
}

export function isThisWeek(date: dayjs.ConfigType): boolean {
  return dayjs(date).isSame(dayjs(), 'week');
}
/**
 * 根据时间戳和当前时间，计算展示的日期信息
 * @param {number} timestamp - 时间戳（毫秒）
 * @returns {string} 格式化后的日期展示信息
 *
 * 规则：
 * * 5分钟内：Just now
 * * 一小时内： 59 minutes ago
 * * 今天内：11:59 AM / 10:24 PM
 * * 今天之前：Apr 15, 2025
 */
export function formatTimeForDisplay(timestamp: number) {
  // 确保传入的是有效的时间戳
  // if (typeof timestamp !== 'number' || isNaN(timestamp)) {
  //   throw new Error('Invalid timestamp');
  // }

  const targetTime = dayjs(timestamp);
  const now = dayjs();

  // 计算时间差（分钟）
  const minutesDiff = now.diff(targetTime, 'minute');

  // 5分钟内：Just now
  if (minutesDiff < 5) {
    return 'Just now';
  }

  // 一小时内：XX minutes ago
  if (minutesDiff < 60) {
    return `${minutesDiff} minutes ago`;
  }

  // 今天内：11:59 AM / 10:24 PM
  if (targetTime.isSame(now, 'day')) {
    return targetTime.format('h:mm A');
  }

  // 今天之前：Apr 15, 2025
  return targetTime.format('MMM D, YYYY');
}

/**
 * 格式化最新动态时间（建联详情页展开列表等场景）
 * English display rules:
 * - Within 24 hours: "X minutes ago" / "X hours ago"
 * - 24h - 3 days: "X days ago"
 * - > 3 days: "Mar 4, 2026" (full date)
 * @param dateTime - 时间戳（毫秒）、数字字符串或 ISO 日期字符串
 * @returns 格式化后的英文时间字符串
 */
export function formatLatestActivityTime(dateTime: dayjs.ConfigType): string {
  try {
    if (dateTime == null || dateTime === '') {
      return '';
    }

    // 如果是纯数字字符串，转为数字
    let parsed: dayjs.Dayjs;
    if (typeof dateTime === 'string' && /^\d+$/.test(dateTime)) {
      parsed = dayjs(Number(dateTime));
    } else {
      parsed = dayjs(dateTime);
    }

    if (!parsed.isValid()) {
      return '';
    }

    const now = dayjs();
    const minutesDiff = now.diff(parsed, 'minute');
    const hoursDiff = now.diff(parsed, 'hour');
    const daysDiff = now.diff(parsed, 'day');

    // 未来时间或无效差值，显示完整日期
    if (minutesDiff < 0) {
      return parsed.format('MMM D, YYYY');
    }

    // Within 24 hours: X minutes ago / X hours ago
    if (hoursDiff < 24) {
      if (minutesDiff < 60) {
        return minutesDiff <= 1 ? '1 minute ago' : `${minutesDiff} minutes ago`;
      }
      return hoursDiff <= 1 ? '1 hour ago' : `${hoursDiff} hours ago`;
    }

    // 24h - 3 days: X days ago
    if (daysDiff < 3) {
      return daysDiff <= 1 ? '1 day ago' : `${daysDiff} days ago`;
    }

    // > 3 days: Mar 4, 2026
    return parsed.format('MMM D, YYYY');
  } catch {
    return '';
  }
}

/**
 * 格式化邮件卡片时间为 "HH:mm MMM D, YYYY" 格式
 * @param {dayjs.ConfigType} dateTime - 时间戳（毫秒，支持数字或字符串）或日期字符串
 * @returns {string} 格式化后的时间，例如: "09:00 Apr 15, 2025"
 */
export function formatEmailCardTime(dateTime: dayjs.ConfigType): string {
  try {
    if (!dateTime) {
      return '';
    }

    // 如果是字符串类型的时间戳（纯数字字符串），转换为数字以便 dayjs 正确处理
    if (typeof dateTime === 'string') {
      try {
        // 使用正则表达式检查是否为纯数字字符串
        if (/^\d+$/.test(dateTime)) {
          const timestamp = Number(dateTime);
          if (isNaN(timestamp)) {
            return '';
          }
          const formatted = dayjs(timestamp).format('HH:mm MMM D, YYYY');
          // 检查格式化结果是否有效（dayjs 无效日期会返回 'Invalid Date'）
          if (formatted === 'Invalid Date') {
            return '';
          }
          return formatted;
        }
      } catch {
        // 正则表达式或转换出错，尝试直接使用 dayjs 解析
      }
    }

    const formatted = dayjs(dateTime).format('HH:mm MMM D, YYYY');
    // 检查格式化结果是否有效
    if (formatted === 'Invalid Date') {
      return '';
    }
    return formatted;
  } catch {
    // 任何错误都返回空字符串，避免页面崩溃
    return '';
  }
}

/**
 * 格式化消息列表时间
 * - 今天：HH:mm（例如 12:12）
 * - 昨天：D MMM（例如 17 Apr）
 * - 今年之前：D MMM, YYYY（例如 17 Apr, 2025）
 * @param {dayjs.ConfigType} dateTime - 时间戳或日期字符串
 * @returns {string} 格式化后的时间
 */
export function formatMessageListTime(dateTime: dayjs.ConfigType): string {
  try {
    if (!dateTime) {
      return '';
    }

    let parsed: dayjs.Dayjs;
    if (typeof dateTime === 'string' && /^\d+$/.test(dateTime)) {
      const timestamp = Number(dateTime);
      if (isNaN(timestamp)) return '';
      parsed = dayjs(timestamp);
    } else {
      parsed = dayjs(dateTime);
    }

    if (!parsed.isValid()) return '';

    const now = dayjs();

    if (parsed.isSame(now, 'day')) {
      return parsed.format('HH:mm');
    }

    if (parsed.isSame(now.subtract(1, 'day'), 'day')) {
      return parsed.format('D MMM');
    }

    if (parsed.isSame(now, 'year')) {
      return parsed.format('D MMM');
    }

    return parsed.format('D MMM, YYYY');
  } catch {
    return '';
  }
}

export function formatDateString(value: string = '', defaultValue = '') {
  const trimmedValue = (value && String(value).trim()) || '';
  const date = dayjs(value);

  if (!trimmedValue || !date.isValid()) {
    return defaultValue;
  }

  // 根据原始值的格式来决定显示格式
  if (/^\d{4}$/.test(trimmedValue)) {
    // 只有年份，显示年份
    return date.format('YYYY');
  } else if (/^\d{4}-\d{1,2}$/.test(trimmedValue)) {
    // 年-月，显示 "Oct, 2025" 格式
    return date.format('MMM, YYYY');
  } else {
    // 完整日期，显示 "Oct 15, 2025" 格式
    return date.format('MMM D, YYYY');
  }
}

/**
 * 将 "YYYY-MM-DD" 格式的日期字符串转为 "MMM D, YYYY" 格式
 * 例如: "2026-03-05" → "Mar 5, 2026"
 * @param dateStr - "YYYY-MM-DD" 格式的日期字符串
 * @param defaultValue - 解析失败时的默认返回值
 */
export function formatYMDToDisplay(dateStr: string, defaultValue = ''): string {
  if (!dateStr) return defaultValue;
  const date = dayjs(dateStr);
  return date.isValid() ? date.format('MMM D, YYYY') : defaultValue;
}

/**
 * 获取当前 date 所在月份的日历起止日期
 * 注：固定返回 6 周（42 天）的日期范围，从周日开始到周六结束
 * 包含上个月月末几天和下个月月初几天
 * @param date
 */
export function getCalendarRange(date: Date): [Dayjs, Dayjs] {
  // 获取当前月的第一天
  const firstDayOfMonth = dayjs(date).startOf('month');
  // 获取日历起始日期（从周日开始，可能是上个月的日期）
  // day() 返回 0-6，0 是周日
  const calendarStartDate = firstDayOfMonth.subtract(firstDayOfMonth.day(), 'day');
  // 固定 6 周（42 天），结束日期为起始日期 + 41 天
  const calendarEndDate = calendarStartDate.add(41, 'day');

  return [calendarStartDate, calendarEndDate];
}

/**
 * 格式化时间为 "HH:mm, MMM D, YYYY" 格式
 * 例如: "10:54, Apr 1, 2025"
 * @param {dayjs.ConfigType} dateTime - 时间戳（毫秒）或日期字符串
 * @returns {string} 格式化后的时间字符串
 */
export function formatHistoryTimestamp(dateTime: dayjs.ConfigType): string {
  try {
    if (!dateTime) {
      return '';
    }

    const formatted = dayjs(dateTime).format('HH:mm, MMM D, YYYY');
    // 检查格式化结果是否有效
    if (formatted === 'Invalid Date') {
      return '';
    }
    return formatted;
  } catch {
    // 任何错误都返回空字符串，避免页面崩溃
    return '';
  }
}
